/**
 * The console's binding of the shared user menu (#1466 slice A).
 *
 * ── the measurement that made this an ADOPTION, not a component ────────────
 * `packages/ui/src/shell/UserMenu.tsx` has existed since #1158 M6, is finished,
 * carries 17 unit specs and a 6-profile browser matrix — and on this fork
 * `grep -rn "UserMenu" apps/console/src | wc -l` returned **0**. The portal
 * renders it through `PortalUserMenu.tsx`; this file is that binding's twin, and
 * it is deliberately shaped line-for-line like it so the next fix to either is
 * obviously the same fix.
 *
 * What the console had instead was **four account concerns spread across a
 * flat eleven-child top bar**, two product controls sitting between them: a
 * personalize-navigation glyph, an unlabelled moon glyph, an inert 18ch email
 * span and a naked sign-out icon beside a notification bell. Nothing was wrong
 * with any one of them; there was no *account surface*, so each one had to be
 * its own affordance in the strip.
 *
 * ── what is session-gated, and what is NOT ─────────────────────────────────
 * This is the one place this binding departs from the portal's, and the reason
 * is a host difference rather than a preference. The console runs in FIXTURE
 * mode with no backend and therefore no session (`app.tsx:24` renders the app
 * anyway; the persona switcher is the identity model there), and the top bar it
 * replaces reflected that exactly: the theme toggle was unconditional, the email
 * and sign-out were inside `{session && …}`.
 *
 * So the menu itself is unconditional — appearance and "Customize navigation"
 * are facts about the person's browser and shell, and both work with
 * no server at all — while `onSignOut`, `onSave` and the identity are passed
 * only where there is a session to end and a door to write through. That is the
 * package's own invariant (`ui-and-experience.md` §1): an affordance a host
 * should not offer is a prop it does not pass. Returning `null` without a
 * session, which is right for the portal, would delete the theme control from
 * the entire demo build.
 *
 * ── the profile read is the SECOND HOST of one verb ────────────────────────
 * `core.my_profile()` lives in `core` and not in `portal` precisely so this host
 * could call it (`apps/portal/src/data.tsx:3477-3480`), and until now it never
 * had. A failed read falls back to the session's own email rather than to
 * nothing: the menu is still how a person signs out, so it must survive a
 * profile read that failed. `settable` is the SERVER's answer to which fields
 * are the principal's to state, and this file does not restate it — a
 * deployment predating the migration renders the identity with no form.
 *
 * ── focus restoration is the host's job, and the seam already exists ───────
 * `UserMenu` hands its trigger back through `triggerRef` because the menu closes
 * before an item's `onSelect` runs, so the launching item is unmounted before
 * the sheet captures `document.activeElement` and closing returns focus to
 * `<body>` (#1466 F-30B, measured in every profile). `AppShell` owns the ref and
 * the sheet; this file only forwards it.
 *
 * ── THE PERSONA SUBPANEL (#2328 §2), AND WHERE ITS THREE FACTS LIVE ────────
 * The acting persona moved here from `ContextDock`, which is deleted with the
 * footer that carried it. Three stores, and the split is ruling 74071 §7:
 *
 *   · the ACTING persona — `app/persona.ts`, `sessionStorage`, tab-local. A
 *     preview context, not an assignment, and its two-tab independence is pinned
 *     by a browser spec.
 *   · pins / default / recent — `app/personaPrefs.ts`, the governed
 *     `core.user_setting` door, keyed by authenticated user.
 *   · the CATALOGUE — `core.persona`, read by `usePersonas()`.
 *
 * ── the list is the CATALOGUE, and that is a reported gap, not a claim ─────
 * #2328 §2 asks for *"autocomplete search over personas the current actor may
 * actually activate"*. Measured: no door answers that question. `core.persona`
 * is readable by any authenticated caller by design (`0030:28`); `core.user_persona`
 * holds the ASSIGNMENTS and is RLS-scoped to the caller, but the console's
 * selector has always been a PREVIEW — since #2032 live experience resolution
 * ignores it entirely and asks the server for the person's own default — and in
 * fixture mode there are no assignment rows at all, because there is no server.
 * So this binding offers the catalogue, exactly as the dock did, and the missing
 * contract (`core.my_personas()` plus an activation door that REFUSES a persona
 * the caller may not hold) is reported with the lane rather than faked with a
 * client-side filter that would look like authority and be none.
 *
 * ── the dirty guard is on the exit, not on the panel ──────────────────────
 * §2: *"Dirty forms invoke the standard unsaved-changes guard before switching."*
 * A persona change re-resolves the experience under the page, so it is one of
 * the exits ruling §10 names. `confirmDiscardUnsaved()` is that guard's
 * imperative half; when there is nothing unsaved it prompts nobody.
 */
import { useEffect, useMemo, useRef, useState, type RefObject } from 'react'
import { Plug, SlidersHorizontal } from 'lucide-react'
import { UserMenu, confirmDiscardUnsaved, useMessage, useTheme, type PersonaChoice } from '@solum/ui'
import {
  SignInSecuritySettings, signInIdentityCustody, useAuth, assuranceOf, useDeviceAccounts,
} from '@solum/auth'
import { serverRefusal } from '@solum/sdk'
import { normalizeOwnProfile } from '@solum/experience'
import { useMyProfile, useSetMyProfile } from '../data/queries'
import { setPersona, usePersona, usePersonas } from '../experience/governed'
import { usePersonaHolders } from '../experience/personas'
import { personaOptions } from './personaOptions'
import { usePersonaPrefs } from './personaPrefs'
import { personaInitialChoice } from './persona'
import { ConsoleRecoveryPanel } from './ConsoleRecoveryPanel'
import { consoleRecoveryUrl } from './consoleRecovery'

/**
 * ── the account switcher (#1053 4b) ────────────────────────────────────────
 *
 * Passed only in LIVE mode, and that is a host decision rather than a
 * defensive one: in fixture mode there is no session, no GoTrue and nothing to
 * switch to, and an entry that signs you out of nothing is the same class of
 * lie as the sign-out control the old top bar correctly withheld.
 *
 * The list comes from the DEVICE (`@solum/auth`'s roster), never from a server
 * answer about which logins belong to one person — see
 * `packages/auth/src/accounts.ts` for the ruling and the reason. Every entry
 * ends this session and starts a fresh authentication; the console's `Gate`
 * pre-fills the address the tab is on its way to.
 */
export function ConsoleUserMenu({ triggerRef, onPersonalizeNav, onConnectedApps, placement, capSlot }: {
  triggerRef?: RefObject<HTMLButtonElement | null>
  /** The rail cap's slot — the same menu's second trigger (#2406). */
  capSlot?: HTMLElement | null
  /* Which side the panel opens on. A host that mounts this in the navigation
   * FOOTER passes `above`: there is nothing below a footer for a downward panel
   * to occupy, and a browser cannot scroll it into view. Forwarded, never
   * decided here. */
  placement?: 'below' | 'above'
  /** Omitted where the shell has no sheet to open — then no item is rendered. */
  onPersonalizeNav?: () => void
  /** Opens the person's Connected apps (#2443 slice 4). Omitted ⇒ no row. */
  onConnectedApps?: () => void
}) {
  const { session, signOut, switchAccount, addAccount } = useAuth()
  const sessionUserId = session?.user.id
  const { mode, setMode, headerTreatment, setHeaderTreatment } = useTheme()
  const profile = useMyProfile()
  const save = useSetMyProfile()
  const devices = useDeviceAccounts()
  const persona = usePersona()
  const personas = usePersonas()
  const holders = usePersonaHolders()
  const prefs = usePersonaPrefs()
  const ownTrigger = useRef<HTMLButtonElement>(null)
  const securityTrigger = triggerRef ?? ownTrigger
  const [securityOpen, setSecurityOpen] = useState(false)
  const securityLabel = useMessage('auth.security.title', 'Sign-in and security')

  /* The catalogue, as choices. `core.persona` carries `key`, `label`, `sort`,
     `origin` and `is_active` and NO description column, so no description is
     passed: #2328 asks for *"localized name plus short governed description"*
     and there is no governed description to render. Fabricating one from the
     key would be this component inventing product copy. The gap is reported. */
  const choices = useMemo<PersonaChoice[]>(() => {
    const rows = ((personas.data ?? []) as Array<{ key?: unknown; label?: unknown; is_active?: unknown }>)
      .filter((row) => row.is_active !== false)
      .map((row) => ({ value: String(row.key ?? ''), label: String(row.label ?? row.key ?? '') }))
      .filter((row) => row.value !== '')
    /* `personaOptions` is the dock's own rule, kept verbatim when the dock was
       deleted: a persona this tab is ALREADY acting as stays visible even when
       the catalogue no longer offers it, because dropping it would render the
       acting value as ABSENT rather than as unusual — and it must not fire for
       the empty persona, which is a live tab's ordinary opening state. */
    return personaOptions(persona, rows).map((row) => ({ key: row.value, label: row.label }))
  }, [persona, personas.data])

  /* A fresh LIVE tab deliberately has no browser persona. Initialise only
     after the governed preference and assignment answers settle: choosing the
     first catalogue row would make presentation look authoritative when it is
     not. `personaInitialChoice` never replaces a deliberate tab-local choice,
     and ordinary live experience resolution continues to omit this preview
     value and resolve the assigned default on the server. */
  const initialPersona = personaInitialChoice({
    active: persona,
    availableKeys: personas.isSuccess ? choices.map((choice) => choice.key) : null,
    preference: prefs.defaultKey,
    preferenceState: prefs.readState,
    assignments: holders.isSuccess
      ? holders.data
        .filter((row) => row.user_id === sessionUserId)
        .map((row) => ({ persona_key: row.persona_key, is_default: row.is_default }))
      : null,
    assignmentState: holders.isSuccess ? 'ready' : holders.isError ? 'refused' : 'loading',
  })
  const initialPersonaKey = initialPersona.state === 'resolved' ? initialPersona.key : null
  useEffect(() => {
    if (!sessionUserId || initialPersonaKey === null) return
    setPersona(initialPersonaKey)
  }, [sessionUserId, initialPersonaKey])

  const row = profile.data
  /* Sparse governed rows may borrow only the session's login address; the
     shared pure mapper keeps every server-owned boundary field on the row. */
  const identity = normalizeOwnProfile(row, session?.user)

  return (<>
    <UserMenu
      profile={identity}
      /* A CLAIM the app already holds — no door, no query, no migration. Absent
         where there is no token, and an absent fact is not a negative fact, so
         no badge is rendered rather than a reassuring one. */
      aal={session ? assuranceOf(session.accessToken) : undefined}
      /* Passed only where `core.set_my_profile` is genuinely reachable.
         `UserMenu` still gates the editor on the server's own `settable`, so
         this is the outer of two boundaries, not a substitute for it. */
      onSave={session ? (name) => save.mutate(name) : undefined}
      saving={save.isPending}
      error={save.error
        ? serverRefusal(save.error, (status) =>
            status === '404'
              ? 'This deployment has not been updated with the profile door yet.'
              : 'That change was refused, and the reason did not come back. Please tell us.')
        : null}
      /* In fixture mode there is no session to end, and a sign-out control that
         does nothing is worse than none (the sentence the old top bar carried at
         Topbar.tsx:63-67 — kept, because it is still the rule). */
      /* Ruling §10 again: signing out is an exit, and #2328 §5 lists it. */
      onSignOut={session ? () => { if (confirmDiscardUnsaved()) void signOut() } : undefined}
      onSecure={session ? () => setSecurityOpen(true) : undefined}
      accounts={session
        ? {
            current: { userId: session.user.id, email: session.user.email },
            /* The roster holds the CURRENT login too — it is written the moment
               a session becomes current — so the "others" list is the roster
               minus whoever is signed in, and never a second row for the same
               person. */
            others: devices
              .filter((a) => a.userId !== session.user.id)
              .map((a) => ({ userId: a.userId, email: a.email })),
            onSwitch: (userId) => { void switchAccount(userId) },
            onAddAccount: () => { void addAccount() },
          }
        : undefined}
      persona={{
        active: persona,
        choices,
        onActivate: (key) => {
          /* Ruling §10's imperative exit. Nothing unsaved ⇒ no prompt at all. */
          if (!confirmDiscardUnsaved()) return
          setPersona(key)
          /* Recent is a PREFERENCE and rides the governed door; the activation
             itself does not, and that asymmetry is ruling §7. */
          prefs.remember(key)
        },
        pinned: prefs.pinned,
        recent: prefs.recent,
        defaultKey: prefs.defaultKey,
        /* Passed only where the governed base was actually read. An unread base
           cannot be written over (`personaPrefs.ts`), so offering Pin there
           would be a control that silently does nothing. */
        onPin: prefs.ready ? prefs.pin : undefined,
        onUnpin: prefs.ready ? prefs.unpin : undefined,
        onSetDefault: prefs.ready ? prefs.setDefault : undefined,
      }}
      appearance={{ mode, onChange: setMode }}
      /* ONE ThemeProvider state, two presentations (#2347 §4). Read from the
         same `useTheme()` call as `mode` directly above it, so the menu and the
         Console's Experience & branding field cannot hold different answers —
         there is no second store for them to disagree through. */
      headerTreatment={{ treatment: headerTreatment, onChange: setHeaderTreatment }}
      /* #2348 §50 — a standard menu row with an icon and a chevron, not a bare
         underlined link. It opens the navigation editor, so the chevron is a
         truthful disclosure mark rather than decoration.

         `Account settings` and `Keyboard shortcuts` are NOT here, and that is
         the design's own refusal rather than an omission: §6 and §7 admit them
         *"only when bound to a real route"* / *"only when their actions exist"*,
         and this console has no account-settings route and no shortcuts surface
         to open. A row that opens nothing is worse than a row that is absent. */
      items={[
        ...(session
          ? [{
            key: 'security',
            label: securityLabel,
            section: 'account' as const,
            onSelect: () => setSecurityOpen(true),
            chevron: true,
          }]
          : []),
        /* #2443 slice 4 — the person's OAuth grants, in the ACCOUNT section:
           a real route (Connected apps), shown only to a signed-in person and
           only where the shell can open it. Additive: the navigation row below
           is unchanged. */
        ...(session && onConnectedApps
          ? [{
            key: 'connected-apps',
            label: 'Connected apps',
            section: 'account' as const,
            onSelect: onConnectedApps,
            icon: <Plug size={15} />,
            chevron: true,
          }]
          : []),
        ...(onPersonalizeNav
          ? [{
            key: 'customize-navigation',
            label: 'Customize navigation',
            onSelect: onPersonalizeNav,
            icon: <SlidersHorizontal size={15} />,
            chevron: true,
          }]
          : []),
      ]}
      triggerRef={securityTrigger}
      placement={placement}
      capSlot={capSlot}
    />
    {securityOpen && session && (
      <SignInSecuritySettings
        identity={{
          email: identity?.email ?? session.user.email ?? null,
          custody: signInIdentityCustody(row?.idp),
        }}
        recoveryRedirectTo={consoleRecoveryUrl() ?? window.location.origin}
        restoreFocusTo={securityTrigger}
        additionalSections={<ConsoleRecoveryPanel onRecovered={() => { void signOut() }} />}
        onClose={() => setSecurityOpen(false)} />
    )}
  </>)
}
